// Unified form submitter.
// Sends both `contact` and `estimate` form payloads to the FastAPI backend,
// which then dispatches lead + autoresponse emails through Brevo (HTTP API).
//
// IMPORTANT: The Emergent preview backend that powers roofing-goral.com is
// periodically hibernated AND fully recycled (whole pod restart, ~15-30s cold
// boot). We retry transient 5xx/network errors up to 4 times with 8s waits so
// the customer never sees a spurious "Something went wrong" for what is really
// a cold-start issue.

import axios from "axios";
import { getApiBase } from "./backend";

const API = getApiBase();

const MAX_RETRIES = 4;
const RETRY_DELAY_MS = 8000; // enough time for a fresh pod to boot on Emergent

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

// Retry only on transient failures: network (no response), 5xx, or 408/429.
// Do NOT retry on 4xx validation errors — those are permanent user errors.
function isTransient(err) {
  if (!err) return false;
  if (!err.response) return true; // network error / CORS / DNS / connection reset
  const s = err.response.status;
  return s >= 500 || s === 408 || s === 429;
}

async function postWithRetry(path, body) {
  let lastErr;
  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    try {
      const { data } = await axios.post(`${API}${path}`, body, { timeout: 30000 });
      return data;
    } catch (err) {
      lastErr = err;
      if (attempt < MAX_RETRIES && isTransient(err)) {
        console.warn(`[submit] transient error on ${path} (attempt ${attempt}/${MAX_RETRIES}), retrying in ${RETRY_DELAY_MS}ms...`, err.response?.status || err.message);
        await sleep(RETRY_DELAY_MS);
        continue;
      }
      throw err;
    }
  }
  throw lastErr;
}

export async function submitContact(form) {
  const data = await postWithRetry("/contact", form);
  return { ok: true, ...data };
}

export async function submitEstimate(form) {
  const data = await postWithRetry("/estimate", form);
  return { ok: true, ...data };
}

export async function submitCalculatorQuote(payload) {
  const data = await postWithRetry("/calculator-quote", payload);
  return { ok: true, ...data };
}
